import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  SimpleGrid,
  Paper,
  Text,
  Group,
  ThemeIcon,
  Title,
  Stack,
  Box,
  Badge,
  Button,
} from "@mantine/core";
import {
  IconBuildingStore,
  IconShirt,
  IconPlus,
  IconClick,
  IconCurrencyRupee,
  IconReceipt2,
  IconClock,
  IconCheck,
  IconX,
  IconChevronRight,
} from "@tabler/icons-react";
import CreateShopModal from "./components/CreateShopModal";
import { API_BASE_URL } from "../../config/baseUrls";

const BASE_URL = `${API_BASE_URL}/shop-owner`;
const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("shopToken")}`,
});

export default function ShopDashboard() {
  const [analytics, setAnalytics] = useState({
    totalRevenue: 0,
    totalSales: 0,
    totalShops: 0,
    totalProducts: 0,
  });
  const [applications, setApplications] = useState([]);
  const [createShopOpen, setCreateShopOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch Aggregated Analytics
    fetch(`${BASE_URL}/dashboard/analytics`, { headers: getHeaders() })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setAnalytics(data.data);
      })
      .catch(console.error);

    // Fetch Applications
    fetch(`${BASE_URL}/dashboard/applications`, { headers: getHeaders() })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setApplications(data.data);
      })
      .catch(console.error);
  }, []);

  const getStatusBadge = (status) => {
    switch (status) {
      case "APPROVED":
        return (
          <Badge
            color="green"
            variant="light"
            size="lg"
            leftSection={<IconCheck size={14} />}
          >
            Active
          </Badge>
        );
      case "PENDING":
        return (
          <Badge
            color="orange"
            variant="light"
            size="lg"
            leftSection={<IconClock size={14} />}
          >
            In Review
          </Badge>
        );
      case "REJECTED":
        return (
          <Badge
            color="red"
            variant="light"
            size="lg"
            leftSection={<IconX size={14} />}
          >
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge color="gray" variant="light" size="lg">
            {status}
          </Badge>
        );
    }
  };

  const handleApplicationClick = (app) => {
    if (app.status === "APPROVED" && app.shopfront.id) {
      navigate(`/shop-owner/shops/${app.shopfront.id}`);
    } else if (app.status === "PENDING") {
      // You could route them to a pending status page, or just alert them
      alert("This store is currently under review by our operations team.");
    }
  };

  return (
    <Stack gap="xl" pb="xl" px={{ base: "xs", sm: "md" }}>
      {/* HEADER */}
      <Box mt="md">
        <Title order={1} size="h1" fw={900}>
          Merchant Dashboard
        </Title>
        <Text c="dimmed" size="lg" mt={4}>
          A complete overview of your earnings and active locations.
        </Text>
      </Box>

      {/* QUICK ACTIONS - Giant Interactive Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
        <Paper
          withBorder
          p="xl"
          radius="md"
          onClick={() => setCreateShopOpen(true)}
          style={{
            cursor: "pointer",
            transition: "all 0.2s ease",
            borderColor: "var(--mantine-color-gray-3)",
          }}
          className="hover:shadow-md hover:border-blue-500 hover:bg-blue-50"
        >
          <Group gap="md">
            <ThemeIcon size={56} radius="md" color="blue" variant="light">
              <IconPlus size={28} />
            </ThemeIcon>
            <Box>
              <Text size="lg" fw={700}>
                Register New Store
              </Text>
              <Text size="sm" c="dimmed">
                Apply for a new event booth or online shop
              </Text>
            </Box>
          </Group>
        </Paper>

        <Paper
          withBorder
          p="xl"
          radius="md"
          onClick={() => navigate("/shop-owner/products")}
          style={{
            cursor: "pointer",
            transition: "all 0.2s ease",
            borderColor: "var(--mantine-color-gray-3)",
          }}
          className="hover:shadow-md hover:border-gray-500 hover:bg-gray-50"
        >
          <Group gap="md">
            <ThemeIcon size={56} radius="md" color="dark" variant="filled">
              <IconClick size={28} />
            </ThemeIcon>
            <Box>
              <Text size="lg" fw={700}>
                Global Inventory
              </Text>
              <Text size="sm" c="dimmed">
                Manage all your products across all stores
              </Text>
            </Box>
          </Group>
        </Paper>
      </SimpleGrid>

      {/* AGGREGATED ANALYTICS (Revenue & Sales) */}
      <Box>
        <Title order={3} size="h2" mb="sm">
          Lifetime Earnings
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <Paper
            withBorder
            p="xl"
            radius="md"
            bg="blue.0"
            style={{ borderColor: "var(--mantine-color-blue-4)" }}
          >
            <Group justify="space-between" align="flex-start">
              <Box>
                <Text c="blue.8" size="sm" tt="uppercase" fw={700} mb={4}>
                  Total Revenue
                </Text>
                <Text
                  fw={900}
                  size="h1"
                  c="blue.9"
                  style={{ fontSize: "2.5rem" }}
                >
                  ₹{analytics.totalRevenue.toLocaleString("en-IN")}
                </Text>
              </Box>
              <ThemeIcon color="blue.6" variant="light" size={64} radius="md">
                <IconCurrencyRupee size={32} />
              </ThemeIcon>
            </Group>
          </Paper>

          <Paper
            withBorder
            p="xl"
            radius="md"
            bg="teal.0"
            style={{ borderColor: "var(--mantine-color-teal-4)" }}
          >
            <Group justify="space-between" align="flex-start">
              <Box>
                <Text c="teal.8" size="sm" tt="uppercase" fw={700} mb={4}>
                  Total Transactions
                </Text>
                <Text
                  fw={900}
                  size="h1"
                  c="teal.9"
                  style={{ fontSize: "2.5rem" }}
                >
                  {analytics.totalSales.toLocaleString()}
                </Text>
              </Box>
              <ThemeIcon color="teal.6" variant="light" size={64} radius="md">
                <IconReceipt2 size={32} />
              </ThemeIcon>
            </Group>
          </Paper>
        </SimpleGrid>
      </Box>

      {/* SECONDARY STATS */}
      <SimpleGrid cols={{ base: 2, sm: 2 }} spacing="lg">
        <Paper withBorder p="xl" radius="md" bg="gray.0">
          <Group gap="md">
            <ThemeIcon size={48} variant="white" color="gray">
              <IconBuildingStore size={24} />
            </ThemeIcon>
            <Box>
              <Text fw={800} size="h2">
                {analytics.totalShops}
              </Text>
              <Text c="dimmed" size="sm" tt="uppercase" fw={600}>
                Active Shops
              </Text>
            </Box>
          </Group>
        </Paper>

        <Paper withBorder p="xl" radius="md" bg="gray.0">
          <Group gap="md">
            <ThemeIcon size={48} variant="white" color="gray">
              <IconShirt size={24} />
            </ThemeIcon>
            <Box>
              <Text fw={800} size="h2">
                {analytics.totalProducts}
              </Text>
              <Text c="dimmed" size="sm" tt="uppercase" fw={600}>
                Products
              </Text>
            </Box>
          </Group>
        </Paper>
      </SimpleGrid>

      {/* MY APPLICATIONS LIST */}
      <Box mt="xl">
        <Title order={3} size="h2" mb="xs">
          My Storefront Applications
        </Title>

        <Stack gap="md">
          {applications.length === 0 ? (
            <Paper withBorder p="xl" radius="md" ta="center" bg="gray.0">
              <Text c="dimmed" size="lg">
                You haven't submitted any store applications yet.
              </Text>
            </Paper>
          ) : (
            applications.map((app) => (
              <Paper
                key={app.id}
                withBorder
                radius="md"
                p="lg"
                onClick={() => handleApplicationClick(app)}
                style={{
                  cursor: app.status === "APPROVED" ? "pointer" : "default",
                  transition: "all 0.2s ease",
                }}
                className={
                  app.status === "APPROVED"
                    ? "hover:shadow-md hover:border-blue-400"
                    : ""
                }
              >
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={700} size="xl" truncate mb={4}>
                      {app.name}
                    </Text>
                    <Group gap="md">
                      <Badge variant="dot" color="gray" size="md">
                        {app.setupType === "EVENT"
                          ? "Physical Event"
                          : "Online Only"}
                      </Badge>
                      <Text size="sm" c="dimmed">
                        Applied:{" "}
                        {new Date(app.createdAt).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </Text>
                    </Group>
                  </Box>

                  <Group gap="lg">
                    {getStatusBadge(app.status)}
                    {app.status === "APPROVED" && (
                      <IconChevronRight
                        size={24}
                        color="var(--mantine-color-gray-5)"
                      />
                    )}
                  </Group>
                </Group>
              </Paper>
            ))
          )}
        </Stack>
      </Box>

      <CreateShopModal
        isOpen={createShopOpen}
        onClose={() => setCreateShopOpen(false)}
        onSuccess={() => window.location.reload()}
      />
    </Stack>
  );
}
